/**
 * Blueprints API Slice
 *
 * RTK Query endpoints for Story Blueprints (event outlines with segments and petals).
 */

import { api } from '../api';

// ============================================
// Types
// ============================================

export type FlowerKey =
  | 'problem'
  | 'pain'
  | 'power'
  | 'product'
  | 'path'
  | 'plan'
  | 'place'
  | 'pushback'
  | 'promise';

export type BouquetKey = 'opening' | 'ambition' | 'bridge' | 'close' | 'ultimate_story';

export type PetalType = 'story' | 'case_study' | 'irrefutable_logic' | 'media' | 'quote' | 'stat';

export type EventType =
  | 'sales_call'
  | 'webinar'
  | 'keynote'
  | 'networking'
  | 'investor_pitch'
  | 'cold_call'
  | 'panel'
  | 'interview'
  | 'other';

export type BlueprintGoal =
  | 'sell'
  | 'book_meeting'
  | 'build_authority'
  | 'make_connection'
  | 'raise_capital'
  | 'hire'
  | 'educate'
  | 'other';

export type StagePosition =
  | 'stage_left'
  | 'stage_center_back'
  | 'stage_right'
  | 'stage_center_front'
  | 'off_stage';

export type AmbitionButton = 'clarity' | 'dissatisfaction' | 'power';

// ============================================
// Entity Types
// ============================================

export interface FlowerDefinition {
  id: string;
  key: FlowerKey;
  label: string;
  description: string;
  ambitionButtons: AmbitionButton[];
}

export interface BouquetDefinition {
  id: string;
  key: BouquetKey;
  label: string;
  description: string;
}

export interface PetalDto {
  id: string;
  userId: string;
  title: string;
  petalType: PetalType;
  body: string;
  sourceLabel?: string;
  domains: string[];
  tags: string[];
  flowerKeys: FlowerKey[];
  createdAt: string;
  updatedAt: string;
}

export interface SegmentPetal {
  id: string;
  title: string;
  petalType: PetalType;
  orderIndex: number;
}

export interface BlueprintSegment {
  id: string;
  blueprintId: string;
  orderIndex: number;
  bouquetKey?: BouquetKey;
  flowerKey: FlowerKey;
  stagePosition: StagePosition;
  notes?: string;
  petals: SegmentPetal[];
}

export interface Blueprint {
  id: string;
  userId: string;
  title: string;
  eventDate?: string;
  eventType: EventType;
  goal: BlueprintGoal;
  domainKey: string;
  isOnStage: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  segments: BlueprintSegment[];
}

export interface BlueprintSummary {
  id: string;
  title: string;
  segments: Array<{
    orderIndex: number;
    flowerKey: FlowerKey;
    bouquetKey?: BouquetKey;
    stagePosition: StagePosition;
    petalTitles: string[];
  }>;
}

// ============================================
// Request Types
// ============================================

export interface CreatePetalRequest {
  title: string;
  petalType: PetalType;
  body: string;
  sourceLabel?: string;
  domains?: string[];
  tags?: string[];
  flowerKeys?: FlowerKey[];
}

export interface UpdatePetalRequest {
  title?: string;
  petalType?: PetalType;
  body?: string;
  sourceLabel?: string;
  domains?: string[];
  tags?: string[];
  flowerKeys?: FlowerKey[];
}

export interface CreateBlueprintRequest {
  title: string;
  eventDate?: string;
  eventType: EventType;
  goal: BlueprintGoal;
  domainKey: string;
  isOnStage?: boolean;
  notes?: string;
  useDefaultTemplate?: boolean;
}

export interface UpdateBlueprintRequest {
  title?: string;
  eventDate?: string;
  eventType?: EventType;
  goal?: BlueprintGoal;
  domainKey?: string;
  isOnStage?: boolean;
  notes?: string;
}

export interface CreateSegmentRequest {
  flowerKey: FlowerKey;
  bouquetKey?: BouquetKey;
  stagePosition?: StagePosition;
  notes?: string;
  orderIndex?: number;
}

export interface UpdateSegmentRequest {
  flowerKey?: FlowerKey;
  bouquetKey?: BouquetKey;
  stagePosition?: StagePosition;
  notes?: string;
}

export interface ReorderSegmentsRequest {
  segmentOrder: Array<{ id: string; orderIndex: number }>;
}

export interface AttachPetalsRequest {
  petalIds: string[];
}

export interface PetalFilters {
  domainKey?: string;
  flowerKey?: FlowerKey;
  petalType?: PetalType;
  tag?: string;
  search?: string;
}

// ============================================
// API Response Types
// ============================================

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================
// Labels / Constants
// ============================================

export const FLOWER_LABELS: Record<FlowerKey, string> = {
  problem: 'Problem',
  pain: 'Pain',
  power: 'Power',
  product: 'Product',
  plan: 'Plan',
  path: 'Path',
  place: 'Place',
  pushback: 'Pushback',
  promise: 'Promise',
};

export const BOUQUET_LABELS: Record<BouquetKey, string> = {
  opening: 'Opening',
  ambition: 'Ambition',
  bridge: 'Bridge',
  close: 'Close',
  ultimate_story: 'Ultimate Story',
};

export const PETAL_TYPE_LABELS: Record<PetalType, string> = {
  story: 'Story',
  case_study: 'Case Study',
  irrefutable_logic: 'Irrefutable Logic',
  media: 'Media',
  quote: 'Quote',
  stat: 'Statistic',
};

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  sales_call: 'Sales Call',
  webinar: 'Webinar',
  keynote: 'Keynote',
  networking: 'Networking',
  investor_pitch: 'Investor Pitch',
  cold_call: 'Cold Call',
  panel: 'Panel',
  interview: 'Interview',
  other: 'Other',
};

export const GOAL_LABELS: Record<BlueprintGoal, string> = {
  sell: 'Close a Sale',
  book_meeting: 'Book a Meeting',
  build_authority: 'Build Authority',
  make_connection: 'Make a Connection',
  raise_capital: 'Raise Capital',
  hire: 'Hire Someone',
  educate: 'Educate',
  other: 'Other',
};

export const STAGE_POSITION_LABELS: Record<StagePosition, string> = {
  stage_left: 'Stage Left',
  stage_center_back: 'Center Back',
  stage_right: 'Stage Right',
  stage_center_front: 'Center Front',
  off_stage: 'Off Stage',
};

export const STAGE_POSITION_ICONS: Record<StagePosition, string> = {
  stage_left: '⬅️',
  stage_center_back: '⬆️',
  stage_right: '➡️',
  stage_center_front: '⭐',
  off_stage: '🎭',
};

// ============================================
// API Slice
// ============================================

export const blueprintsApi = api.injectEndpoints({
  endpoints: (builder) => ({

    // ==================
    // Petals
    // ==================
    getPetals: builder.query<PetalDto[], PetalFilters | void>({
      query: (filters) => ({
        url: '/petals',
        params: filters || undefined,
      }),
      transformResponse: (response: ApiResponse<PetalDto[]>) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Petals' as const, id })),
              { type: 'Petals', id: 'LIST' },
            ]
          : [{ type: 'Petals', id: 'LIST' }],
    }),

    createPetal: builder.mutation<PetalDto, CreatePetalRequest>({
      query: (body) => ({
        url: '/petals',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<PetalDto>) => response.data,
      invalidatesTags: [{ type: 'Petals', id: 'LIST' }],
    }),

    updatePetal: builder.mutation<PetalDto, { id: string; data: UpdatePetalRequest }>({
      query: ({ id, data }) => ({
        url: `/petals/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<PetalDto>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Petals', id },
        { type: 'Petals', id: 'LIST' },
      ],
    }),

    deletePetal: builder.mutation<void, string>({
      query: (id) => ({
        url: `/petals/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Petals', id },
        { type: 'Petals', id: 'LIST' },
        { type: 'Blueprints', id: 'LIST' }, // Petals may be removed from blueprints
      ],
    }),

    // ==================
    // Blueprints
    // ==================
    getBlueprints: builder.query<Blueprint[], void>({
      query: () => '/blueprints',
      transformResponse: (response: ApiResponse<Blueprint[]>) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Blueprints' as const, id })),
              { type: 'Blueprints', id: 'LIST' },
            ]
          : [{ type: 'Blueprints', id: 'LIST' }],
    }),

    getBlueprint: builder.query<Blueprint, string>({
      query: (id) => `/blueprints/${id}`,
      transformResponse: (response: ApiResponse<Blueprint>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Blueprints', id }],
    }),

    getBlueprintSummary: builder.query<BlueprintSummary, string>({
      query: (id) => `/blueprints/${id}/summary`,
      transformResponse: (response: ApiResponse<BlueprintSummary>) => response.data,
    }),

    createBlueprint: builder.mutation<Blueprint, CreateBlueprintRequest>({
      query: (body) => ({
        url: '/blueprints',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<Blueprint>) => response.data,
      invalidatesTags: [{ type: 'Blueprints', id: 'LIST' }],
    }),

    updateBlueprint: builder.mutation<Blueprint, { id: string; data: UpdateBlueprintRequest }>({
      query: ({ id, data }) => ({
        url: `/blueprints/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<Blueprint>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Blueprints', id },
        { type: 'Blueprints', id: 'LIST' },
      ],
    }),

    deleteBlueprint: builder.mutation<void, string>({
      query: (id) => ({
        url: `/blueprints/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Blueprints', id },
        { type: 'Blueprints', id: 'LIST' },
      ],
    }),

    // ==================
    // Segments
    // ==================
    addSegment: builder.mutation<BlueprintSegment, { blueprintId: string; data: CreateSegmentRequest }>({
      query: ({ blueprintId, data }) => ({
        url: `/blueprints/${blueprintId}/segments`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<BlueprintSegment>) => response.data,
      invalidatesTags: (_result, _error, { blueprintId }) => [{ type: 'Blueprints', id: blueprintId }],
    }),

    updateSegment: builder.mutation<BlueprintSegment, { segmentId: string; data: UpdateSegmentRequest }>({
      query: ({ segmentId, data }) => ({
        url: `/segments/${segmentId}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<BlueprintSegment>) => response.data,
      invalidatesTags: ['Blueprints'],
    }),

    deleteSegment: builder.mutation<void, { segmentId: string; blueprintId: string }>({
      query: ({ segmentId }) => ({
        url: `/segments/${segmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { blueprintId }) => [{ type: 'Blueprints', id: blueprintId }],
    }),

    reorderSegments: builder.mutation<void, { blueprintId: string; data: ReorderSegmentsRequest }>({
      query: ({ blueprintId, data }) => ({
        url: `/blueprints/${blueprintId}/segments/reorder`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { blueprintId }) => [{ type: 'Blueprints', id: blueprintId }],
    }),

    attachPetalsToSegment: builder.mutation<BlueprintSegment, { segmentId: string; data: AttachPetalsRequest }>({
      query: ({ segmentId, data }) => ({
        url: `/segments/${segmentId}/petals`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<BlueprintSegment>) => response.data,
      invalidatesTags: ['Blueprints'],
    }),
  }),
  overrideExisting: false,
});

// ============================================
// Exported Hooks
// ============================================

export const {
  // Petals
  useGetPetalsQuery,
  useCreatePetalMutation,
  useUpdatePetalMutation,
  useDeletePetalMutation,
  // Blueprints
  useGetBlueprintsQuery,
  useGetBlueprintQuery,
  useGetBlueprintSummaryQuery,
  useCreateBlueprintMutation,
  useUpdateBlueprintMutation,
  useDeleteBlueprintMutation,
  // Segments
  useAddSegmentMutation,
  useUpdateSegmentMutation,
  useDeleteSegmentMutation,
  useReorderSegmentsMutation,
  useAttachPetalsToSegmentMutation,
} = blueprintsApi;
